import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { MassMailModal } from "screens/mass-mail";
import type { HelpdeskEmail } from "screens/mass-mail/types";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import type { IncomingTenantItem } from "shared/types/rental-contracts";

interface RentalIncomingMassMailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  item: IncomingTenantItem;
}

const RentalIncomingMassMailModal = ({
  isOpen,
  onClose,
  onSuccess,
  item,
}: RentalIncomingMassMailModalProps) => {
  const { session } = useSession();
  const [emailHistory, setEmailHistory] = useState<HelpdeskEmail[]>([]);

  useEffect(() => {
    if (!isOpen || !session) return;

    const fetchHistory = async () => {
      try {
        const [historyRes] = await Promise.all([
          fetchWithAuth(`${API_URL}/email/rental-incoming/${item.applicationId}/emails`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/email/rental-incoming/${item.applicationId}/emails/mark-read`, { method: "PUT" }, session),
        ]);
        if (historyRes.ok) {
          const emails = await historyRes.json();
          setEmailHistory(emails);
        }
      } catch (error) {
        console.error("Failed to fetch rental incoming email history:", error);
      }
    };

    fetchHistory();
  }, [isOpen, session, item.applicationId]);

  const initialState = useMemo(() => ({
    contacts: [{
      id: String(item.applicationId),
      name: `${item.firstName} ${item.lastName}`,
      resourceId: String(item.applicationId),
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      units: [],
    }],
    emailSubject: `[RENT-${item.applicationId}] - `,
    senderEmail: 'ella@yally.be',
    autoSelectFirstTemplate: false,
  }), [item.applicationId, item.firstName, item.lastName, item.email]);

  if (!isOpen) return null;

  return (
    <MassMailModal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onSuccess}
      initialState={initialState}
      emailHistory={emailHistory}
    />
  );
};

export default RentalIncomingMassMailModal;
